// Event-log text for champion events (D11: summoned / fell). Pure; the shared
// formatter in ui/RightPanel/formatEventPayload.ts delegates here so the
// big switch there does not grow.
//
// Events: the intent events `legend:summon_started` / `legend:ability_activated`
// and the milestone events (`milestone:reached` with `legend.summoned` /
// `legend.fallen`). Milestones of every other id return null (unchanged).

import type { MessageKey, Translate } from '../i18n/index.js';
import { ticksToSeconds } from './championRules.js';
import { useChampionUi } from './championStore.js';

const KNOWN_ABILITY_KEYS = new Set(['firestorm', 'bulwark', 'withering', 'ascend']);

/**
 * The ability key of a `legend:ability_activated` event. The event carries
 * `key` (and `kind`) since the protocol fix; snapshots replaying older events
 * only have the slot, so fall back to the local champion's ability list the
 * provider keeps in the store.
 */
function abilityKeyOf(payload: Payload): string | null {
  const key = payload?.key;
  if (typeof key === 'string') return key;
  const slot = payload?.slot;
  if (typeof slot !== 'number') return null;
  return useChampionUi.getState().localAbilityKeys[slot] ?? null;
}

type Payload = Record<string, unknown> | undefined;

/** Label key for a champion event, or null when it is not one. */
export function championEventLabelKey(type: string, payload: Payload): MessageKey | null {
  switch (type) {
    case 'legend:summon_started':
      return 'event.legendSummonStarted';
    case 'legend:ability_activated': {
      const key = abilityKeyOf(payload);
      return key && KNOWN_ABILITY_KEYS.has(key)
        ? (`event.legend.ability.${key}` as MessageKey)
        : 'event.legend.ability.generic';
    }
    case 'milestone:reached':
      if (payload?.milestoneId === 'legend.summoned') return 'event.legendSummoned';
      if (payload?.milestoneId === 'legend.fallen') return 'event.legendFallen';
      return null;
    default:
      return null;
  }
}

/** Detail line for a champion event, or null when it is not one. */
export function championEventDetail(t: Translate, type: string, payload: Payload): string | null {
  const player = ((payload?.playerIndex as number | undefined) ?? 0) + 1;
  switch (type) {
    case 'legend:summon_started':
      return t('event.detail.legendSummonStarted', { player });
    case 'legend:ability_activated': {
      const key = abilityKeyOf(payload);
      const detail = key && KNOWN_ABILITY_KEYS.has(key) ? key : 'generic';
      return t(`event.detail.legend.${detail}` as MessageKey, {
        hits: String(payload?.hits ?? 0),
        kills: String(payload?.kills ?? 0),
      });
    }
    case 'milestone:reached': {
      const data = (payload?.data as Record<string, unknown> | undefined) ?? {};
      if (payload?.milestoneId === 'legend.summoned') {
        const kind = data.legend === undefined ? null : String(data.legend);
        return t('event.detail.legendSummoned', {
          player,
          kind: kind ? t(`champion.kind.${kind}` as MessageKey) : t('champion.title'),
        });
      }
      if (payload?.milestoneId === 'legend.fallen') {
        const respawn = Number(data.respawnTick ?? 0);
        const now = Number(payload?.tick ?? 0);
        return t('event.detail.legendFallen', {
          player,
          seconds: ticksToSeconds(Math.max(0, respawn - now)),
        });
      }
      return null;
    }
    default:
      return null;
  }
}
